import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import {
  CollectionsViewModel,
  CollectionViewDispatcher,
  useCollectionsViewModel,
} from './collections.viewModel';
import { SalesType } from '@client/new/common/types';
import { WithRouterProps } from 'react-router';
import { TitledPage } from '@client/new/components/Misc';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { Collections } from './collections';
import { Button as MuiButton } from '@mui/material';
import { PATHS, navigate } from '@client/routes';
import {
  BasicMenu,
  BasicMenuItem,
} from '@client/new/components/BasicMenu/BasicMenu';
import { getCurrentPath } from '@client/helpers/routes';
import { OtpEmptyState } from './components/otpEmptyState';
import { OtpsMigrationAlert } from './components/OtpsMigrationAlert';
import { SubscriptionEmptyState } from './components/subscriptionEmptyState';
import { Button, ButtonLink } from '@design-system/components/button/button';
import { CollectionTypeModal } from './collectionTypeModal';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Typography } from '@design-system/components/typography/typography';
import { LinksModal } from '../../common/linksModal/linksModal';
import { ProgramLinksModal } from '../programs/view/components/programLinksModal';
import { ArchiveModal } from '@client/new/common/collection/ArchiveModal';
import { DeleteModal } from '@client/new/common/collection/DeleteModal';
import programIcon from '/static/images/program-icon.svg';
import collectionIcon from '/static/images/collection-icon.svg';
import uploadIcon from '/static/images/video-library-header.svg';
import { ConfirmExitModal } from './components/ConfirmExitModal';
import { ProgramUploadModal } from '@client/new/pages/collections/components/programUploadModal';
import { PublishToBundle } from '../collection/PublishToBundleModal';
import { RemoveFromBundle } from '../collection/RemoveFromBundleModal';
import { SetupBundleModal } from './setupBundle/SetupBundleModal';
import { pageBackground } from '@client/new/pages/page-background.css';

export type CollectionsContentProps = {
  viewModel: CollectionsViewModel;
  salesType: SalesType;
};

export const CollectionsContent = ({
  viewModel,
  salesType,
}: CollectionsContentProps) => {
  switch (viewModel.mode.type) {
    case 'empty': {
      if (salesType === 'otp') {
        return (
          <OtpEmptyState
            {...viewModel.emptyState.otp}
            to={viewModel.createLink}
          />
        );
      }
      return <SubscriptionEmptyState {...viewModel.emptyState.subscription} />;
    }

    case 'drafts-only':
      return (
        <>
          <Collections
            dispatch={viewModel.dispatch}
            listOptions={viewModel.listOptions}
            selectedList={viewModel.selectedList}
            smartTableProps={viewModel.mode.value}
            programDuplication={viewModel.programDuplication}
          />
        </>
      );
    case 'standard':
      return (
        <Collections
          dispatch={viewModel.dispatch}
          listOptions={viewModel.listOptions}
          selectedList={viewModel.selectedList}
          smartTableProps={viewModel.mode.value}
          programDuplication={viewModel.programDuplication}
        />
      );
    default:
      return null;
  }
};

const toSubscriptionMenuItems = (
  creatorId: number,
  salesType: SalesType,
  showExperimentalCta: boolean,
  dispatch: CollectionViewDispatcher,
): BasicMenuItem[] => [
  {
    testId: 'subscriptions-create-program',
    label: 'Program',
    url: PATHS.NEW_PROGRAM(creatorId, {
      salesType,
      from: getCurrentPath(true),
    }),
    icon: <img src={programIcon} alt="Program icon" />,
  },
  {
    testId: 'subscriptions-create-category',
    label: 'Category',
    url: PATHS.NEW_CATEGORY(creatorId, {
      from: getCurrentPath(true),
    }),
    icon: <img src={collectionIcon} alt="Category icon" />,
  },
  ...(showExperimentalCta
    ? [
        {
          label: 'Upload',
          onClick: () => dispatch({ type: 'openProgramUploadModal' }),
          icon: <img src={uploadIcon} alt="Upload icon" />,
        },
      ]
    : []),
];

const ListPageCTA = ({
  salesType,
  creatorId,
  showCreate,
  dispatch,
  showLinksButton,
  showExperimentalCta,
}: {
  creatorId: number;
  salesType: SalesType;
  showCreate: boolean;
  dispatch: CollectionViewDispatcher;
  showLinksButton: boolean;
  showExperimentalCta: boolean;
}) => {
  switch (salesType) {
    case 'otp':
      return (
        <MuiButton
          variant="contained"
          sx={{
            backgroundColor: 'primary.dark',
            height: '36px',
            paddingY: '6px',
          }}
          onClick={() =>
            navigate(
              PATHS.NEW_PROGRAM(creatorId, {
                salesType,
                from: getCurrentPath(true),
              }),
            )
          }
        >
          Create
        </MuiButton>
      );
    case 'subscription':
      return (
        <div className="flex items-center gap-6">
          {showLinksButton ? (
            <Button
              onClick={() => dispatch({ type: 'subLinksModal' })}
              variant="text"
            >
              <Typography
                variant="body"
                size="large"
                className={clsx(
                  'flex gap-2 items-center',
                  sprinkles({ color: 'P-80' }),
                )}
              >
                <FontAwesomeIcon icon={faLink} />
                Links
              </Typography>
            </Button>
          ) : null}
          <ButtonLink to={PATHS.LANDING_PAGE(creatorId)} color="primaryTonal">
            Landing Page
          </ButtonLink>
          {!showCreate ? null : (
            <BasicMenu
              testId="subscriptions-create-menu"
              title="Create"
              items={toSubscriptionMenuItems(
                creatorId,
                salesType,
                showExperimentalCta,
                dispatch,
              )}
            />
          )}
        </div>
      );
  }
};

const ListsPage = (p: WithRouterProps & { salesType: SalesType }) => {
  const viewModel = useCollectionsViewModel({
    type: p.salesType,
    influencerId: p.params.influencerId,
    salesType: p.salesType,
    locationSearch: p.location.search,
  });
  const creatorId = p.params.influencerId
    ? Number(p.params.influencerId)
    : undefined;

  const pageTitle =
    p.salesType === 'otp' ? 'One-Time Purchases' : 'Subscription';

  return (
    <div className={pageBackground}>
      <AsyncRenderer
        data={viewModel}
        loadRender={
          <TitledPage title={pageTitle}>
            <TableLoader />
          </TitledPage>
        }
        renderer={(data) => (
          <TitledPage
            title={pageTitle}
            rightContent={
              creatorId ? (
                <ListPageCTA
                  creatorId={creatorId}
                  salesType={p.salesType}
                  showCreate={data.showCreate}
                  dispatch={data.dispatch}
                  showLinksButton={data.subLinksModal.showButton}
                  showExperimentalCta={data.showExperimentalProgramUpload}
                />
              ) : null
            }
          >
            {p.salesType === 'otp' && (
              <OtpsMigrationAlert visible={data.isOtpMigratedToBundle} />
            )}
            <CollectionsContent viewModel={data} salesType={p.salesType} />
            {!creatorId ? null : (
              <CollectionTypeModal
                creatorId={creatorId}
                isOpen={data.selectionModal.open}
                onClose={() => data.selectionModal.close()}
              />
            )}
            {p.salesType === 'otp' ? (
              <ProgramLinksModal {...data.otpLinksModal} />
            ) : (
              <LinksModal {...data.linksModal} />
            )}
            <LinksModal {...data.subLinksModal} />
            <ArchiveModal {...data.archiveModal} />
            <DeleteModal {...data.deleteModal} />
            <ConfirmExitModal {...data.confirmExitModal} />
            <ProgramUploadModal {...data.programUploadModal} />
            <PublishToBundle {...data.publishToBundleModal} />
            <RemoveFromBundle {...data.removeFromBundleModal} />
            <SetupBundleModal {...data.setupBundleModal} />
          </TitledPage>
        )}
      />
    </div>
  );
};

export const OTPListPage = (p: WithRouterProps) => (
  <ListsPage {...p} salesType={'otp'} />
);

export const SubscriptionListPage = (p: WithRouterProps) => (
  <ListsPage {...p} salesType={'subscription'} />
);
